import { useState } from 'react';

interface StaffStory {
  id: number;
  name: string;
  role: string;
  years: string;
  image: string;
  quote: string;
  story: string;
  highlights: string[];
  fullStory?: {
    title: string;
    lead: string;
    profile: {
      name: string;
      age: string;
      location: string;
      background: string;
    };
    sections: {
      heading: string;
      content: string;
    }[];
  };
}

const staffStories: StaffStory[] = [
  {
    id: 1,
    name: '田中 優子',
    role: '保育士',
    years: '勤続3年目',
    image: 'https://static.readdy.ai/image/8d13d909127b965a1b96285c4b9af1e2/f097c8aa42f851b713d9e34af047d295.png',
    quote: '駅まで走る毎日と、汗ばんだ我が子の手。38歳、「片道1時間の満員電車」を降りて見つけたもの。',
    story: '朝7時。まだ少し眠そうな我が子の着替えを済ませ、時計を気にしながら朝食を急かす。「ほら、あと一口だけ食べて!バスの時間になっちゃうよ！」近くの保育園に預け、手を振る我が子に「お迎え急ぐからね！」と言い残して駅へと走る。改札を通る瞬間、ふと自分の手を見ると、さっきまで繋いでいた子どもの手の温もりと、少しの汗の感覚が残っている。「私は一体、誰のために、何のために、こんなに急いでいるんだろう」大規模園で10年。時短勤務をしながら、仕事と育児の「時間の壁」にぶつかっていた時、なぜ「きらり保育園 箕面」を選んだのか。',
    highlights: ['大規模園から転職', '地元エリアで働く', '時短勤務でも充実'],
    fullStory: {
      title: '駅まで走る毎日と、汗ばんだ我が子の手。38歳、「片道1時間の満員電車」を降りて見つけたもの。',
      lead: '朝7時。まだ少し眠そうな我が子の着替えを済ませ、時計を気にしながら朝食を急かす。「ほら、あと一口だけ食べて!バスの時間になっちゃうよ！」近くの保育園に預け、手を振る我が子に「お迎え急ぐからね！」と言い残して駅へと走る。改札を通る瞬間、ふと自分の手を見ると、さっきまで繋いでいた子どもの手の温もりと、少しの汗の感覚が残っている。「私は一体、誰のために、何のために、こんなに急いでいるんだろう」大規模園で10年。時短勤務をしながら、仕事と育児の「時間の壁」にぶつかっていた時、なぜ「きらり保育園 箕面」を選んだのか。',
      profile: {
        name: '田中優子',
        age: '38歳',
        location: '箕面市在住',
        background: '大阪市内の大規模園で10年勤務。落ち着いた環境で、我が子との時間も大切に働く小学生2人の子を持つママさん保育士。'
      },
      sections: [
        {
          heading: '「時短勤務」という限られた時間の中で、追い詰められていた日々',
          content: '前職は、100名を超える園児が通う活気ある大規模園でした。活気があるからこそ、一人ひとりの子どもとじっくり向き合うには、どうしても物理的な限界がありました。特に私は時短勤務。決められた退勤時間までに、日々の保育だけでなく、連絡帳の記入や指導案の作成、行事の準備を終わらせなければなりません。「もっとこの子の話を聞いてあげたい」「この遊びの続きを一緒に見守りたい」そう思っても、常に頭の片隅では「あと15分で事務作業を終わらせないと、お迎えに間に合わない」という焦りがありました。大規模園のダイナミックな保育は好きでしたが、限られた時間の中で、自分の理想とする「一人ひとりに寄り添う保育」ができないことへのもどかしさが、少しずつ積み重なっていったんです。'
        },
        {
          heading: '「地元エリアで働く」ことで、朝の景色が変わった',
          content: '転職を考えたとき、一番に重視したのは「自分自身の生活の余白」でした。その軸をもって転職活動をした結果、片道1時間かけていた満員電車を降り、自転車で通える「きらり保育園 箕面」で働くことにしました。朝、駅まで全力疾走する必要はなくなりました。子どもと手をつないで歩きながら、道端に咲いている花を眺める余裕ができました。職場が近い。それだけで、私の心から「常に何かに追われている感覚」が消えていったんです。'
        },
        {
          heading: '「感覚」を「科学」する。ベテランの私を支えた新しい視点',
          content: 'きらり保育園に入って驚いたのは、独自のメソッド『きらりの発達ナビ』でした。これまでは長年の「経験」や「勘」で判断していた子どもの発育を、ここでは科学的な根拠に基づいて見守ります。例えば、時短勤務で子どもと接する時間が限られていても、この「共通言語」があるおかげで、他のスタッフとの情報共有がスムーズになり、短い時間でも深く子どもを理解できるようになりました。効率化されているからこそ、持ち帰り仕事やサービス残業もありません。保育士が「一人の人間」としての生活を大切にしながら、プロとして輝ける場所だと実感しています。'
        },
        {
          heading: '誰かのために、自分を削っているあなたへ',
          content: '大規模園で揉まれてきた経験は、私の宝物です。でも、今の私には「箕面の街で、家族との時間を守りながら、子どもたち一人ひとりの成長を支える」という働き方が、一番しっくりきています。お昼前に漂ってくる、美味しい出汁の香り。スタッフ同士の「いつもありがとうございます」という言葉。もし、あなたが今、忙しすぎる毎日の中で「もっと丁寧に働きたい」と願っているなら、きらり保育園は最適な場所になると感じています。'
        }
      ]
    }
  },
  {
    id: 2,
    name: '山本 莉子',
    role: '保育士',
    years: '勤続2年目',
    image: 'https://static.readdy.ai/image/8d13d909127b965a1b96285c4b9af1e2/fd3f4ad560bfa0f1a5cd31ca1243cdba.png',
    quote: '「先輩より先に帰りづらい」環境は一切なし！タイパ重視の私が、箕面の保育園で"スマートな成長"を選んだ理由。',
    story: '「山本さん、まだ残るの？」「あ、はい。先輩方もまだいらっしゃるので……」前職の園では、自分の仕事が終わっていても、なんとなく帰りづらい空気が漂っていました。手書きの膨大な連絡帳に、終わりの見えない行事準備。「保育は、子どものためにできることをすべてやってこそ一人前」そんな根性論に、20代の大切な時間を使い切っていいのかな？と、スマホを眺めながら溜息をつく毎日でした。そんな私が、今では、定時上がりで推し活もプライベートも楽しみながら、保育士として確かな手応えを感じています。',
    highlights: ['定時退勤が当たり前', 'プライベート充実', 'スマートな働き方'],
    fullStory: {
      title: '「先輩より先に帰りづらい」環境は一切なし！タイパ重視の私が、箕面の保育園で"スマートな成長"を選んだ理由。',
      lead: '「山本さん、まだ残るの？」\n「あ、はい。先輩方もまだいらっしゃるので……」\n前職の園では、自分の仕事が終わっていても、なんとなく帰りづらい空気が漂っていました。\n手書きの膨大な連絡帳に、終わりの見えない行事準備。\n「保育は、子どものためにできることをすべてやってこそ一人前」\nそんな根性論に、20代の大切な時間を使い切っていいのかな？と、スマホを眺めながら溜息をつく毎日でした。\n\nそんな私が、今では、定時上がりで推し活もプライベートも楽しみながら、保育士として確かな手応えを感じています。\nきらり保育園で見つけた、「新しい働き方」についてお話しします。',
      profile: {
        name: '山本 莉子',
        age: '24歳',
        location: '豊中市または大阪市内在住',
        background: '新卒入社の園で2年目。古い慣習やサービス残業は絶対NG。梅田へのアクセスも重視しつつ、スマートに働きたい現代っ子。'
      },
      sections: [
        {
          heading: '「なんとなく」で進む保育に、ずっとモヤモヤしていた',
          content: '新卒で入った園は、いわゆる「伝統的な」場所でした。保育の内容も、先輩の「勘」や「経験」がすべて。「あの子、最近ちょっと落ち着かないね」「そうだね、愛情が足りないのかもね」…。そんな曖昧な会話に、ずっと違和感がありました。もっと客観的に、論理的に、子どもたちの成長を捉える方法はないのかな？と。\n\nICT化も進んでおらず、重いバインダーを持ち歩き、昼休憩の時間も削ってペンを走らせる。「もっとスマートに、子どもと向き合えるはずなのに」そのモヤモヤが、きらり保育園の求人を見たときに「ここだ！」という直感に変わりました。'
        },
        {
          heading: '「感覚」を「科学」する。納得感のある成長',
          content: 'きらり保育園に入って一番衝撃を受けたのは、独自メソッドの『きらりの発達ナビ』です。ここでは、子どもの「今」をデータと根拠に基づいて分析します。「なぜこの子が今、こういう行動をするのか」を、発達の視点からロジカルに説明できるんです。\n\n「なんとなく」ではなく「根拠がある」から、若手の私でも自信を持って保護者の方にアドバイスができる。先輩たちのアドバイスも、「私のときはこうだった」という昔話ではなく、「このデータに基づくと、今はこういうアプローチがベストだね」と、常にアップデートされたもの。この納得感こそが、今の私の成長を加速させてくれています。'
        },
        {
          heading: 'タイパの良さが、心の余裕を生む',
          content: 'きらり保育園は、驚くほど「無駄」を嫌います。事務作業はタブレットで入力。行事のために徹夜で壁面装飾を作る…なんて文化もありません。「限られた時間の中で、いかに質の高い保育をするか」に全員が集中しているので、残業も持ち帰り仕事も、本当にゼロ。\n\n18：30に園を出て、カフェで勉強したり、ライブに行ったり。自分の時間をしっかり確保できるからこそ、翌朝また新鮮な気持ちで子どもたちに「おはよう！」が言える。「仕事も大事だけど、自分の人生も同じくらい大事」。そんなことが、当たり前に尊重されています。'
        },
        {
          heading: 'アップデートし続けるチーム',
          content: '「ありがとう」という言葉が、一日に何度も飛び交うのもこの園の特徴です。若手もベテランも関係なく、効率的なアイデアがあれば「それいいね！」とすぐに採用される。古い常識に縛られず、常に「もっと良くするには？」を考え続けるフラットな空気感があります。\n\nもし、あなたが今、「保育の仕事は好きだけど、この働き方は一生続けられない」と感じているなら。一度、私たちの「スマートな保育」を見に来ませんか？身を削るのではなく、賢く、楽しく、そして本気で子どもと向き合う。そんな新しい保育士のスタイルを、一緒にアップデートしていきましょう！'
        }
      ]
    }
  },
  {
    id: 3,
    name: '松本 香織',
    role: '保育補助',
    years: '勤続1年目',
    image: 'https://static.readdy.ai/image/8d13d909127b965a1b96285c4b9af1e2/dadc0990f41323c536e90b8772ac605e.png',
    quote: '「ママ」以外の名前で呼ばれる時間。ブランク7年、無資格の私が箕面で見つけた、もう一つの居場所。',
    story: '子どもを幼稚園に送り出し、誰もいなくなったリビングで一息つく。ふと静まり返った部屋を見渡し、「あ、最近、ほとんど大人と話してないかも」と思う瞬間がありました。妊娠・出産を機に仕事を辞めて7年。履歴書の「職歴」は止まったまま。「私に何ができるんだろう」「今さら社会に戻れるのかな」そんな不安を抱えながら、スーパーの求人チラシを眺めていた私が、自宅から自転車で10分の「きらり保育園」の門を叩くまでのお話をお伝えします。',
    highlights: ['ブランク7年から復帰', '無資格でもOK', '子育て経験が活きる'],
    fullStory: {
      title: '「ママ」以外の名前で呼ばれる時間。ブランク7年、無資格の私が箕面で見つけた、もう一つの居場所。',
      lead: '子どもを幼稚園に送り出し、誰もいなくなったリビングで一息つく。\nふと静まり返った部屋を見渡し、「あ、最近、ほとんど大人と話してないかも」と思う瞬間がありました。\n\n妊娠・出産を機に仕事を辞めて7年。\n履歴書の「職歴」は止まったまま。\n「私に何ができるんだろう」\n「今さら社会に戻れるのかな」\n\nそんな不安を抱えながら、スーパーの求人チラシを眺めていた私が、自宅から自転車で10分の「きらり保育園」の門を叩くまでのお話をお伝えします。',
      profile: {
        name: '松本 香織',
        age: '32歳',
        location: '箕面市(園近隣)在住',
        background: '育児のため現在は専業主婦。以前はIT業界で事務職を経験。箕面の落ち着いた環境で子育て中。資格はないが、地域の子どもに関わる仕事で社会復帰したい教育熱心なママ。'
      },
      sections: [
        {
          heading: '130万円の壁と、それ以上に高かった「心の壁」',
          content: '社会復帰を考えたとき、一番に考えたのは「家庭との両立」でした。扶養の範囲内で働きたい。でも、ただ時間を切り売りするような仕事ではなく、自分の子育て経験が少しでも活かせる場所がいい。\n\n近所の保育園なら……と思っても、資格がない自分が行ってもいいのか、若い先生たちの足手まといにならないか、そんなことばかり考えていました。でも、毎日のお迎えや買い物帰りに通りかかる「きらり保育園」から伝わってくる、どこか温かい雰囲気がずっと気になっていたんです。'
        },
        {
          heading: '給食の「出汁の香り」が、私の背中を押してくれた',
          content: '勇気を出して見学に行った日。園内に足を踏み入れた瞬間、ふわっと漂ってきたのは、料亭のようなお出汁のいい香りでした。「ここでは、子どもたちの体を守るために、お米やお出汁、調味料からこだわっているんです」\n\nその説明を聞いたとき、毎日必死に家族の食事を作ってきた自分のこれまでの時間が、肯定されたような気がしました。「資格があるかどうかより、子どもを想う気持ちがあるかどうか。松本さんの子育ての経験は、ここでは立派なスキルですよ」先生のその言葉に、ずっと止まっていた私の時間が、ようやく動き出した気がしました。'
        },
        {
          heading: '「お手伝い」ではなく、一人の「プロ」として',
          content: '現在は保育補助として、子どもたちと関わっています。驚いたのは、無資格の私にも、子どもたちの成長を科学的に見る『きらりの発達ナビ』の考え方を丁寧に教えてくれることでした。\n\n「この子は今、こういう動きをしているから、次はこれができるようになるんですよ」感覚で接していた子育てに、確かな「理由」が加わっていく。それは、毎日の家事や育児では得られなかった、知的な刺激と成長の実感でした。ここでは「補助さん」ではなく、一人の大切なチームメンバーとして名前で呼ばれ、頼りにされる。そのことが、こんなにも自分の自信に繋がるとは思っていませんでした。'
        },
        {
          heading: '箕面の街で、夕飯の買い物をして帰る幸せ',
          content: '今は、夕方に仕事を終え、園の駐輪場で先生たちと「お疲れ様！」と笑い合ってから、近くのスーパーへ向かいます。「今日の給食のメニュー、家でも作ってみようかな」なんて考えながら自転車を漕ぐ時間は、以前の「ただ忙しいだけの夕方」とは全く違う、充実した時間です。\n\n夕食のとき、子どもに「今日ね、保育園でこんなことがあったんだよ」と話すと、「ママ、楽しそうだね」と言われます。\n\nもし、あなたが今、「何かを始めたいけど、私なんて」と立ち止まっているなら一度、私たちの園を覗きに来てみませんか？完璧じゃなくてもいい。子育てに悩み、笑い、奮闘してきたあなたの経験が、ここでは誰かの宝物になります。'
        }
      ]
    }
  },
  {
    id: 4,
    name: '中村 亜美',
    role: '看護師',
    years: '勤続1年目',
    image: 'https://static.readdy.ai/image/8d13d909127b965a1b96285c4b9af1e2/09d02e21e96893180438e1f0dcf9f14c.png',
    quote: '「処置」に追われる夜勤を卒業。急性期病院の看護師だった私が、箕面の保育園で『発育のプロ』を目指した理由。',
    story: '朝一番、病棟の廊下を走る足音と、鳴り止まないナースコールの音。「中村さん、次あっちの点滴ね！」モニターの波形と数字に追いかけられ、患者さんの顔をゆっくり見る間もなく終わる12時間夜勤。明けの朝、重い体を引きずって帰宅し、泥のように眠る…そんな毎日の中で、ふと鏡に映った自分の顔があまりに無表情で、怖くなったことがありました。「私は、誰かの人生を『守る』ために看護師になったはずなのに。」そんな葛藤を抱えていた私が、白衣を脱ぎ、箕面の街にある「きらり保育園」でエプロンに袖を通すまで。命と向き合う場所を変えて見つけた、新しい看護の形をお話しします。',
    highlights: ['夜勤なし', '医療知識が活きる', '規則正しい生活'],
    fullStory: {
      title: '「処置」に追われる夜勤を卒業。急性期病院の看護師だった私が、箕面の保育園で『発育のプロ』を目指した理由。',
      lead: '朝一番、病棟の廊下を走る足音と、鳴り止まないナースコールの音。\n「中村さん、次あっちの点滴ね！」\nモニターの波形と数字に追いかけられ、患者さんの顔をゆっくり見る間もなく終わる12時間夜勤。\n明けの朝、重い体を引きずって帰宅し、泥のように眠る…\nそんな毎日の中で、ふと鏡に映った自分の顔があまりに無表情で、怖くなったことがありました。\n\n「私は、誰かの人生を『守る』ために看護師になったはずなのに。」\n\nそんな葛藤を抱えていた私が、白衣を脱ぎ、箕面の街にある「きらり保育園」でエプロンに袖を通すまで。\n命と向き合う場所を変えて見つけた、新しい看護の形をお話しします。',
      profile: {
        name: '中村 亜美',
        age: '29歳',
        location: '吹田市または箕面市在住',
        background: '北摂エリアの急性期病院で夜勤ありの勤務を経験。落ち着いた環境で子どもとじっくり関わりたい看護師。'
      },
      sections: [
        {
          heading: '「救う」ことの誇りと、それ以上に募った「日常」への渇望',
          content: '病院での仕事は、間違いなくやりがいがありました。命の瀬戸際に立ち会い、回復していく姿を見るのは看護師冥利に尽きます。でも、病院で会う患者さんは、みんな「病気」というフィルターを通した姿。退院した後の彼らが、どんな風に笑い、どんな風に日々を過ごしているのか。それを知ることはできませんでした。\n\n特に子どもと接するとき、「病気になってから会う」のではなく「病気にならない体を作り、健やかに育つ過程を支えたい」という想いが強くなってっきました。でも、忙しい現場では、一人ひとりの成長を願う心の余裕さえ、いつの間にか削り取られていました。'
        },
        {
          heading: '保育園は「医療の最前線」だった',
          content: 'きらり保育園に来て一番驚いたのは、ここが想像以上に「科学的」で「専門的」な場所だったことです。単に「怪我がないか見守る」だけが看護師の仕事ではありませんでした。\n\nここでは、『きらりの発達ナビ』というメソッドを使い、子どもの発育を分析します。「この歩き方は、足のどの筋肉が未発達だからかな？」「この離乳食の進め方は、口腔機能の発達に合っているかな？」病院で培った医学知識が、子どもの「未来」を作るための材料として、次々と活かされていく。「治す」看護から「育む」看護へ。視点が変わった瞬間、仕事が一層おもしろくなりました。'
        },
        {
          heading: 'お出汁の香りと、規則正しい生活',
          content: '転職して、何より変わったのは自分の体調です。今は、夜勤もオンコールもありません。朝、保育園の近くを自転車で通り抜けるとき、爽やかな風を感じる。そんな当たり前のことが、どれほど贅沢かを知りました。\n\nお昼前、園内に漂う優しいお出汁の香り。無農薬のお米や無添加の調味料。「体は食べたものでできている」という、医療の基本。それを毎日、子どもたちと一緒に給食を食べながら実感できる環境。夜勤明けのコンビニ弁当で済ませていた頃の自分に、「こんなに穏やかな働き方もあるんだよ」と教えてあげたいくらいです。'
        },
        {
          heading: '看護師として、ずっと続けられる環境を',
          content: 'きらり保育園で働く看護師は少数です。だからこそ、スタッフや保護者の方から「看護師のあみ先生がいてくれて安心です」と言われる責任は大きいです。\n\n流行中の感染症への対策から、個別の発育相談まで。「あみ先生、うちの子の離乳食、これでいいかな？」そうやってお母さんたちに寄り添い、共に成長を喜ぶ。ここは、私が看護師として、そして一人の人間として、長く、深く根を張って働ける場所です。\n\nもしあなたが今、緊張感の続く現場で少しだけ息苦しさを感じているなら。白衣を脱いで、きらり保育園に遊びに来てみませんか？子どもたちのキラキラした瞳の先に、あなたが本当にやりたかった看護が、きっと見つかるはずです。'
        }
      ]
    }
  }
];

export default function SeniorStories() {
  const [activeTab, setActiveTab] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const activeStaff = staffStories[activeTab];

  return (
    <>
      <section className="py-24 px-6 bg-gradient-to-b from-white to-teal-50/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-teal-100 text-teal-700 rounded-full text-sm font-medium mb-4">
              STAFF VOICE
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              先輩の声
            </h2>
            <div className="w-20 h-1 bg-teal-600 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              実際に「おためし」から始めた先輩たちのリアルなストーリーをご紹介します
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {staffStories.map((staff, index) => (
              <button
                key={staff.id}
                onClick={() => setActiveTab(index)}
                className={`flex items-center gap-3 px-5 py-3 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeTab === index
                    ? 'bg-teal-600 text-white shadow-lg shadow-teal-600/30'
                    : 'bg-white text-gray-700 hover:bg-teal-50 border border-gray-200'
                }`}
              >
                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                  <img
                    src={staff.image}
                    alt={staff.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <span className="font-medium text-sm">{staff.name}</span>
              </button>
            ))}
          </div>

          {/* Story Content */}
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="grid md:grid-cols-2">
              {/* Image Side */}
              <div className="relative h-80 md:h-auto">
                <img
                  src={activeStaff.image}
                  alt={activeStaff.name}
                  className="w-full h-full object-cover object-top min-h-[400px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <p className="text-2xl font-bold mb-1">{activeStaff.name}</p>
                  <p className="text-teal-300 font-medium">{activeStaff.role} / {activeStaff.years}</p>
                </div>
              </div>

              {/* Content Side */}
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <div className="mb-6">
                  <div className="w-10 h-10 flex items-center justify-center text-teal-600 mb-4">
                    <i className="ri-double-quotes-l text-4xl"></i>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 leading-relaxed mb-6">
                    {activeStaff.quote}
                  </p>
                </div>

                <p className="text-gray-600 leading-relaxed mb-8">
                  {activeStaff.story}
                </p>

                <div className="space-y-3 mb-6">
                  <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider">ポイント</p>
                  <div className="flex flex-wrap gap-2">
                    {activeStaff.highlights.map((highlight, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 text-teal-700 rounded-full text-sm"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-check-line"></i>
                        </div>
                        {highlight}
                      </span>
                    ))}
                  </div>
                </div>

                {activeStaff.fullStory && (
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="inline-flex items-center gap-2 text-teal-600 font-semibold hover:text-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <span>もっと読む</span>
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Navigation Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {staffStories.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveTab(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                  activeTab === index
                    ? 'bg-teal-600 w-8'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`View story ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Full Story Modal */}
      {isModalOpen && activeStaff.fullStory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full overflow-hidden">
                  <img
                    src={activeStaff.image}
                    alt={activeStaff.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <p className="font-bold text-gray-900">{activeStaff.name}</p>
                  <p className="text-sm text-gray-600">{activeStaff.role} / {activeStaff.years}</p>
                </div>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-gray-600"></i>
              </button>
            </div>

            {/* Modal Content */}
            <div className="px-8 py-10">
              {/* Title */}
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-8">
                {activeStaff.fullStory.title}
              </h2>

              {/* Lead */}
              <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-r-xl mb-10">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {activeStaff.fullStory.lead}
                </p>
              </div>

              {/* Profile */}
              <div className="bg-gray-50 rounded-2xl p-6 mb-10">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-user-line text-teal-600"></i>
                  </div>
                  人物紹介
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>{activeStaff.fullStory.profile.name}</strong>（{activeStaff.fullStory.profile.age}・{activeStaff.fullStory.profile.location}）</p>
                  <p className="text-sm">{activeStaff.fullStory.profile.background}</p>
                </div>
              </div>

              {/* Story Sections */}
              <div className="space-y-10">
                {activeStaff.fullStory.sections.map((section, idx) => (
                  <div key={idx}>
                    <h3 className="text-xl font-bold text-gray-900 mb-4 pb-3 border-b-2 border-teal-600">
                      {section.heading}
                    </h3>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {section.content}
                    </p>
                  </div>
                ))}
              </div>

              {/* Close Button */}
              <div className="mt-12 text-center">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-8 py-3 bg-teal-600 text-white font-semibold rounded-full hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  閉じる
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
